import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook, Twitter } from "lucide-react";
import { LOGOS, api } from "@/lib/api";
import { useStoreInfo } from "@/context/StoreInfoContext";

const INFO = [
  { to: "/about", label: "About Us" },
  { to: "/events", label: "In-Store Events" },
  { to: "/new-releases", label: "New Releases" },
  { to: "/contact", label: "Contact" },
];

const POLICY = [
  { slug: "shipping", label: "Shipping Policy" },
  { slug: "returns", label: "Returns & Refunds" },
  { slug: "privacy", label: "Privacy Policy" },
  { slug: "terms", label: "Terms of Service" },
];

export default function Footer() {
  const store = useStoreInfo();
  const [env, setEnv] = useState("sandbox");
  const [shopLinks, setShopLinks] = useState([]);
  useEffect(() => {
    api.get("/square/config").then((r) => setEnv(r.data.environment || "sandbox")).catch(() => {});
    api
      .get("/categories")
      .then((r) => setShopLinks(r.data.slice(0, 8).map((c) => ({ slug: c.slug, label: c.name }))))
      .catch(() => {});
  }, []);
  const isProd = env === "production";
  return (
    <footer className="bg-brand-navy-dark text-white" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-3" data-testid="footer-logo-link">
              <div className="rounded-xl bg-white px-3 py-2">
                <img src={LOGOS.full} alt="Elevation Gaming Co." className="h-9 w-auto" />
              </div>
            </Link>
            <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/70">
              Elevation Gaming Co. is your local hub for trading card games, tabletop, miniatures,
              and the community that makes them great. Come play, learn, and level up.
            </p>
            <div className="mt-6 space-y-2 text-sm">
              {store.address_full && (
                <p className="flex items-center gap-2 text-white/80" data-testid="footer-address">
                  <MapPin size={14} className="text-brand-teal shrink-0" /> {store.address_full}
                </p>
              )}
              {store.phone && (
                <p className="flex items-center gap-2 text-white/80" data-testid="footer-phone">
                  <Phone size={14} className="text-brand-teal shrink-0" />
                  <a href={`tel:${store.phone.replace(/[^0-9+]/g, "")}`} className="hover:text-white">
                    {store.phone}
                  </a>
                </p>
              )}
              {store.email && (
                <p className="flex items-center gap-2 text-white/80" data-testid="footer-email">
                  <Mail size={14} className="text-brand-teal shrink-0" />
                  <a href={`mailto:${store.email}`} className="hover:text-white">
                    {store.email}
                  </a>
                </p>
              )}
              {store.hours && (
                <p className="flex items-center gap-2 text-white/80" data-testid="footer-hours">
                  <Clock size={14} className="text-brand-teal shrink-0" /> {store.hours}
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">Shop</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {shopLinks.map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/shop/${l.slug}`}
                    className="text-white/75 transition hover:text-white"
                    data-testid={`footer-shop-${l.slug}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">Info</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {INFO.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-white/75 transition hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-brand-teal">Policies</h4>
            <ul className="mt-4 space-y-2 text-sm">
              {POLICY.map((l) => (
                <li key={l.slug}>
                  <Link
                    to={`/policies/${l.slug}`}
                    className="text-white/75 transition hover:text-white"
                    data-testid={`footer-policy-${l.slug}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex gap-3" data-testid="footer-socials">
              {store.instagram_url ? (
                <a
                  href={store.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Instagram${store.instagram_username ? ` — @${store.instagram_username}` : ""}`}
                  className="rounded-full border border-white/20 p-2 text-white/80 transition hover:border-brand-teal hover:text-brand-teal"
                  data-testid="footer-social-instagram"
                >
                  <Instagram size={16} />
                </a>
              ) : null}
              {store.facebook_url ? (
                <a
                  href={store.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="rounded-full border border-white/20 p-2 text-white/80 transition hover:border-brand-teal hover:text-brand-teal"
                  data-testid="footer-social-facebook"
                >
                  <Facebook size={16} />
                </a>
              ) : null}
              {store.tiktok_url ? (
                <a
                  href={store.tiktok_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`TikTok${store.tiktok_username ? ` — @${store.tiktok_username}` : ""}`}
                  className="rounded-full border border-white/20 p-2 text-white/80 transition hover:border-brand-teal hover:text-brand-teal"
                  data-testid="footer-social-tiktok"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M19.6 5.5a5.9 5.9 0 0 1-4-1.5v10.6a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .9.1v2.9a2.5 2.5 0 1 0 1.7 2.4V2h2.8a5.9 5.9 0 0 0 4 3.5Z"/>
                  </svg>
                </a>
              ) : null}
              {store.twitter_url ? (
                <a
                  href={store.twitter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Twitter${store.twitter_username ? ` — @${store.twitter_username}` : ""}`}
                  className="rounded-full border border-white/20 p-2 text-white/80 transition hover:border-brand-teal hover:text-brand-teal"
                  data-testid="footer-social-twitter"
                >
                  <Twitter size={16} />
                </a>
              ) : null}
              {!store.instagram_url && !store.facebook_url && !store.tiktok_url && !store.twitter_url && (
                <span className="text-xs text-white/40" data-testid="footer-socials-empty">
                  Add socials in Square to connect
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-6 text-xs text-white/50 sm:flex-row sm:items-center">
          <p>© {new Date().getFullYear()} Elevation Gaming Co. · elevationgamingco.com · All rights reserved.</p>
          <p className="flex items-center gap-2">
            <span className="inline-flex h-2 w-2 rounded-full bg-brand-teal" />
            Powered by Square Catalog Sync <span className="text-white/30">·</span>
            <span className="rounded-full border border-white/20 px-2 py-0.5">
              {isProd ? "LIVE" : "SANDBOX"}
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}
